"use client";

import { useEffect, useMemo, useState } from "react";

import { StepNavigation } from "@/components/backoffice/step-navigation";
import { VehicleAssignmentStepProgress } from "@/components/backoffice/cat/vehicle-assignment/vehicle-assignment-step-progress";
import { VehicleAssignmentRetailContent } from "@/components/backoffice/cat/vehicle-assignment/retail/vehicle-assignment-retail-steps";
import { useStep } from "@/hooks/use-step-handler";
import { getRetailVehicleAssignmentVisibleDefinitions } from "@/lib/cat/vehicle-assignment-retail-step-definitions";
import {
  toVehicleAssignmentProgressSteps,
  type VehicleAssignmentFlowContext,
} from "@/lib/cat/vehicle-assignment-step-definitions";
import type { Kvps } from "@/lib/kvps";

function VehicleAssignmentRetailFlow({
  currentKvps,
}: {
  currentKvps?: Kvps | null;
}) {
  const { handleStepChange, step } = useStep();
  const [canProceed, setCanProceed] = useState(true);

  const ctx: VehicleAssignmentFlowContext = useMemo(
    () => ({ kvps: currentKvps ?? null }),
    [currentKvps],
  );

  const visibleDefinitions = useMemo(
    () => getRetailVehicleAssignmentVisibleDefinitions(ctx),
    [ctx],
  );

  const stepsRetail = useMemo(
    () => toVehicleAssignmentProgressSteps(visibleDefinitions),
    [visibleDefinitions],
  );

  const maxStep = stepsRetail.length;
  const minStep = 1;

  useEffect(() => {
    if (step > maxStep) {
      handleStepChange(maxStep);
    }
  }, [handleStepChange, maxStep, step]);

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-4 gap-8">
        <div className="col-span-1">
          <VehicleAssignmentStepProgress
            currentStep={step}
            steps={stepsRetail}
          />
        </div>

        <div className="col-span-3">
          <VehicleAssignmentRetailContent
            currentKvps={currentKvps}
            onCanProceedChange={setCanProceed}
          />
          <StepNavigation
            isNextButtonDisabled={!canProceed}
            onNext={() => handleStepChange(Math.min(step + 1, maxStep))}
            onPrevious={() => handleStepChange(Math.max(step - 1, minStep))}
            showNextButton={step < maxStep}
            showPreviousButton={step > minStep}
          />
        </div>
      </div>
    </div>
  );
}

export { VehicleAssignmentRetailFlow };
